import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '@/hooks/use-media-query';

interface MediaItem {
  attachment_id: string;
  file_url: string;
  file_name: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  thumbnail_url?: string;
  created_at: string;
  message_id: string;
  signed_url?: string;
  thumbnail_signed_url?: string;
  user_name?: string;
}

interface MediaCarouselProps {
  isOpen: boolean;
  onClose: () => void;
  initialMediaId: string;
  messageId?: string;  // Optional: If provided, only show media from this message
  roomName: string;
  onViewAllMedia?: () => void;  // Callback to view all media
}

export const MediaCarousel: React.FC<MediaCarouselProps> = ({
  isOpen,
  onClose,
  initialMediaId,
  messageId,
  roomName,
  onViewAllMedia,
}) => {
  const [allMedia, setAllMedia] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Hide header actions when carousel is open
  useEffect(() => {
    if (isOpen) {
      // Add a class to hide header actions
      document.body.classList.add('media-carousel-open');
    } else {
      document.body.classList.remove('media-carousel-open');
    }

    return () => {
      document.body.classList.remove('media-carousel-open');
    };
  }, [isOpen]);

  // Fetch media based on messageId or all media for the conversation
  useEffect(() => {
    if (!isOpen || (!messageId && !roomName)) return;

    const fetchAllMedia = async () => {
      setLoading(true);

      try {
        let messages;

        if (messageId) {
          // If messageId is provided, only fetch that specific message
          const { data } = await supabase
            .from('messages')
            .select('id, created_at, user_name')
            .eq('id', messageId)
            .single();

          messages = data ? [data] : [];
        } else {
          // Otherwise fetch all messages with attachments in the room
          const { data } = await supabase
            .from('messages')
            .select('id, created_at, user_name')
            .eq('room_name', roomName)
            .eq('has_attachments', true)
            .order('created_at', { ascending: true });

          messages = data || [];
        }

        if (!messages || messages.length === 0) {
          setAllMedia([]);
          setLoading(false);
          return;
        }

        // Get session for auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error('No session found');
          setAllMedia([]);
          setLoading(false);
          return;
        }

        // Fetch attachments for all messages
        const attachmentPromises = messages.map(async (message) => {
          try {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments?message_id=${message.id}`,
              {
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              // Add user_name to each attachment
              return Array.isArray(data) ? data.map(att => ({
                ...att,
                user_name: message.user_name,
                message_created_at: message.created_at
              })) : [];
            }
            return [];
          } catch (err) {
            console.error('Error fetching attachments:', err);
            return [];
          }
        });

        const allAttachmentArrays = await Promise.all(attachmentPromises);
        const allAttachments = allAttachmentArrays.flat();

        // Filter to only viewable files (images, videos, audio, PDFs)
        const mediaOnly = allAttachments.filter(att =>
          ['image', 'video', 'audio', 'pdf'].includes(att.file_type) && att.signed_url
        );

        // Sort by created_at ascending (oldest first)
        mediaOnly.sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        setAllMedia(mediaOnly);

        // Find the index of the initial media
        const initialIndex = mediaOnly.findIndex(m => m.attachment_id === initialMediaId);
        setCurrentIndex(initialIndex >= 0 ? initialIndex : 0);
      } catch (error) {
        console.error('🎬 MediaCarousel: Error fetching media:', error);
        setAllMedia([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllMedia();
  }, [isOpen, roomName, initialMediaId, messageId]);

  // Preload adjacent images
  useEffect(() => {
    if (allMedia.length === 0) return;

    const preloadImage = (index: number) => {
      if (index >= 0 && index < allMedia.length) {
        const item = allMedia[index];
        if (item.file_type === 'image' && item.signed_url) {
          const img = new Image();
          img.src = item.signed_url;
        }
      }
    };

    // Preload next and previous images
    preloadImage(currentIndex - 1);
    preloadImage(currentIndex + 1);
  }, [currentIndex, allMedia]);

  const handlePrevious = () => {
    setImageLoading(true);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : allMedia.length - 1));
    setTimeout(() => setImageLoading(false), 200);
  };

  const handleNext = () => {
    setImageLoading(true);
    setCurrentIndex((prev) => (prev < allMedia.length - 1 ? prev + 1 : 0));
    setTimeout(() => setImageLoading(false), 200);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, currentIndex]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrevious,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleDownload = async () => {
    const item = allMedia[currentIndex];
    if (!item?.signed_url) return;

    try {
      const response = await fetch(item.signed_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[10000]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Full screen backdrop with blur */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
      {/* Click outside to close */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-label="Close media viewer"
      />

      {loading ? (
        <motion.div
          className="flex h-full items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </motion.div>
      ) : allMedia.length === 0 ? (
        <motion.div
          className="flex h-full items-center justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-6">
            <p className="text-white">No media found in this conversation</p>
          </div>
        </motion.div>
      ) : (
        <motion.div
          className="relative h-full w-full flex flex-col pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          {...swipeHandlers}
        >
          {/* Header with close button and group indicator */}
          <motion.div
            className={cn(
              "absolute z-20 pointer-events-auto flex items-center justify-between",
              isMobile ? "top-2 left-2 right-2" : "top-4 left-4 right-4"
            )}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {/* Group indicator and view all button if viewing message attachments */}
            {messageId && allMedia.length > 0 && (
              <div className="flex items-center gap-2">
                <div className={cn(
                  "bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg",
                  isMobile ? "text-sm" : "text-base"
                )}>
                  <span className="font-medium text-gray-900">
                    Message attachments ({allMedia.length})
                  </span>
                </div>

                {/* View All Media button */}
                <button
                  onClick={() => {
                    if (onViewAllMedia) {
                      onViewAllMedia();
                    }
                  }}
                  className={cn(
                    "bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg hover:bg-white transition-colors flex items-center gap-2",
                    isMobile ? "text-sm" : "text-base"
                  )}
                  title="View all media in conversation"
                >
                  <svg
                    className={cn(
                      "text-gray-700",
                      isMobile ? "h-4 w-4" : "h-5 w-5"
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  <span className="font-medium text-gray-900">View All</span>
                </button>
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Close button */}
            <button
              onClick={onClose}
              className={cn(
                "rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-colors group",
                isMobile ? "h-10 w-10" : "h-12 w-12"
              )}
              title="Close (Esc)"
            >
              <X className={cn(
                "text-gray-700 group-hover:text-gray-900",
                isMobile ? "h-5 w-5" : "h-6 w-6"
              )} />
            </button>
          </motion.div>

          {/* Media Content - centered and mobile optimized */}
          <div className={cn(
            "flex-1 flex flex-col items-center justify-center pointer-events-none",
            isMobile ? "p-2 pb-[100px]" : "p-8 pb-[160px]" // Reduced bottom padding on mobile
          )}>
            {currentMedia && (
              <motion.div
                key={currentMedia.attachment_id}
                className="relative pointer-events-auto flex flex-col items-center justify-center w-full"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: imageLoading ? 0 : 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Clean media display without overlays */}
                {currentMedia.file_type === 'image' ? (
                  <img
                    src={currentMedia.signed_url}
                    alt={currentMedia.file_name}
                    className={cn(
                      "object-contain select-none rounded-lg",
                      isMobile ? "max-w-full max-h-[65vh]" : "max-w-[85vw] max-h-[60vh]" // Increased height on mobile
                    )}
                    draggable={false}
                    onLoad={() => setImageLoading(false)}
                  />
                ) : currentMedia.file_type === 'video' ? (
                  <video
                    ref={videoRef}
                    src={currentMedia.signed_url}
                    controls
                    autoPlay
                    className={cn(
                      "rounded-lg",
                      isMobile ? "max-w-full max-h-[65vh]" : "max-w-[85vw] max-h-[60vh]" // Increased height on mobile
                    )}
                  />
                ) : currentMedia.file_type === 'audio' ? (
                  <div className={cn(
                    "bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6",
                    isMobile ? "w-full max-w-sm" : "min-w-[320px] max-w-[500px]"
                  )}>
                    <p className="text-white font-medium mb-4 truncate">{currentMedia.file_name}</p>
                    <audio
                      src={currentMedia.signed_url}
                      controls
                      autoPlay
                      className="w-full"
                    />
                  </div>
                ) : currentMedia.file_type === 'pdf' ? (
                  <iframe
                    src={currentMedia.signed_url}
                    className={cn(
                      "rounded-lg bg-white",
                      isMobile ? "w-full h-[65vh]" : "w-[85vw] h-[60vh]" // Increased height on mobile
                    )}
                    title={currentMedia.file_name}
                  />
                ) : null}

                {/* Info and Download bar below media */}
                {currentMedia && (
                  <motion.div
                    className={cn(
                      "bg-white/90 backdrop-blur-sm rounded-lg shadow-lg flex items-center justify-between",
                      isMobile ? "mt-2 px-3 py-2 gap-2" : "mt-4 px-4 py-3 gap-4" // Less margin on mobile
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.15 }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "font-medium text-gray-900 truncate",
                        isMobile ? "text-sm" : "text-base"
                      )}>
                        {currentMedia.user_name || 'Unknown'}
                      </p>
                      <p className={cn(
                        "text-gray-600",
                        isMobile ? "text-xs" : "text-sm"
                      )}>
                        {format(new Date(currentMedia.created_at), isMobile ? 'MMM d • h:mm a' : 'MMM d, yyyy • h:mm a')}
                      </p>
                    </div>
                    <button
                      onClick={handleDownload}
                      className={cn(
                        "rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors group flex-shrink-0",
                        isMobile ? "h-9 w-9" : "h-10 w-10"
                      )}
                      title="Download"
                    >
                      <Download className={cn(
                        "text-gray-700 group-hover:text-gray-900",
                        isMobile ? "h-4 w-4" : "h-5 w-5"
                      )} />
                    </button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>

          {/* Navigation Arrows - Only show if more than one media */}
          {allMedia.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors pointer-events-auto"
                aria-label="Previous media"
              >
                <ChevronLeft className="h-5 w-5 text-gray-700" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors pointer-events-auto"
                aria-label="Next media"
              >
                <ChevronRight className="h-5 w-5 text-gray-700" />
              </button>
            </>
          )}

          {/* Bottom Bar with Thumbnails - positioned fixed at bottom */}
          {allMedia.length > 1 && (
            <motion.div
              className={cn(
                "absolute z-20 pointer-events-auto",
                isMobile ? "bottom-1 left-2 right-2" : "bottom-4 left-4 right-4" // Moved closer to bottom on mobile
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
            >
              <div className={cn(
                "bg-white/90 backdrop-blur-sm rounded-lg shadow-lg",
                isMobile ? "p-2" : "p-3"
              )}>
                <div className="flex items-center justify-center gap-2">
                  {/* Compact thumbnail strip */}
                  <div className="flex items-center gap-1 overflow-x-auto max-w-[90vw]">
                    {allMedia.map((item, index) => (
                      <button
                        key={item.attachment_id}
                        onClick={() => {
                          setImageLoading(true);
                          setCurrentIndex(index);
                          setTimeout(() => setImageLoading(false), 200);
                        }}
                        className={cn(
                          "flex-shrink-0 rounded overflow-hidden transition-all",
                          isMobile ? "h-8 w-8" : "h-10 w-10",
                          index === currentIndex
                            ? "ring-2 ring-primary scale-110"
                            : "opacity-60 hover:opacity-100"
                        )}
                      >
                        {item.file_type === 'image' ? (
                          <img
                            src={item.thumbnail_signed_url || item.signed_url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                            <span className={cn(
                              isMobile ? "text-[10px]" : "text-xs"
                            )}>
                              {item.file_type === 'video' ? '🎥' :
                               item.file_type === 'audio' ? '🎵' : '📄'}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Counter */}
                  <div className={cn(
                    "text-gray-600 flex-shrink-0 px-2",
                    isMobile ? "text-xs" : "text-sm"
                  )}>
                    {currentIndex + 1}/{allMedia.length}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};